// Адрес раздела «Люди». Правило 4: в адресе кодируются раздел, представление, отбор,
// строка, вкладка карточки и открытая панель — ссылку коллега открывает в том же состоянии.
//
//   /people/employees                          чистый адрес: вкладка «На вахте»
//   /people/employees?f.status=all             все состояния сразу
//   /people/employees?f.status=fired           вкладка «Уволены» (любой код состояния работает так же)
//   /people/employees?f.debt=true              только с непогашенным долгом, по ВСЕМ состояниям
//   /people/employees?f.site=<id>&q=старцев    объект плюс поиск, тоже по всем состояниям
//   /people/type-<id>                          представление по типу оформления, по всем состояниям
//   /people/type-none                          люди без проставленного типа
//   /people/employees/r/<id>?tab=documents     карточка человека, вкладка «Документы»
//   ?p=new-employee:new                        мастер «Новый сотрудник» поверх раздела
//
// ## Какая вкладка состояния открыта: решает ТОЛЬКО адрес
//
// Вкладки «На вахте · Межвахта · Резерв · Уволены · Все» живут в адресе (правило 4), и то, какая
// из них открыта, выводится из адреса одной функцией `registryTab`. Не из загруженных данных:
// иначе вкладка мигала бы — сначала «Все», потом «На вахте», когда приедет ответ сервера.
//
//   - состояние названо в адресе (`f.status=<код>` или `f.status=all`) — открыто оно;
//   - не названо, но выборка чем-то сужена (поиск, объект, регион, разряд, долг, срез по типу
//     оформления) — открыты ВСЕ. Человек, который ищет или отбирает, не ждёт, что часть людей
//     спрятана: ссылки из «Дел» и уведомлений (`?f.debt=true`) работают так же, как до вкладок;
//   - не названо и ничего не сужено (чистый адрес) — «На вахте».
//
// Почему «На вахте», а не «Все»: заказчик (финдиректор, 29.09.2026) открывает раздел ради тех,
// кто сейчас на вахте, — из 315 человек их 133, а остальные «всё сразу» мешают. Терять остальных
// при этом нельзя, поэтому они в одном нажатии, с числами.
//
// Почему представление по типу оформления адресуется идентификатором, а не понятным словом:
// типы оформления — ДАННЫЕ администратора («Штат», «Договор», «Самозанятый»), их набор и
// названия меняются в системе, а не в коде. Слаг, собранный из названия, разъехался бы с
// первым же переименованием и сломал бы уже отправленные ссылки.
//
// Чистые функции без React: их гоняют тесты и переиспользуют все экраны раздела.

import { isEmployeeStatus, type EmployeeStatus } from '@/shared/api/endpoints/people';
import { buildPath, parsePath } from '@/shared/routing/paths';
import { buildSearch, parseSearch, pushPanel, type SearchState } from '@/shared/routing/search';

export const SECTION = 'people';

/** Представление «весь реестр». Оно же стоит в реестре разделов как стартовое. */
export const VIEW_ALL = 'employees';
/** Префикс представлений по типу оформления. */
const TYPE_PREFIX = 'type-';
/** Представление «без проставленного типа оформления». */
export const VIEW_NO_TYPE = `${TYPE_PREFIX}none`;

/**
 * Что за представление открыто. `all` — весь реестр; `employment` — срез по типу
 * оформления, где typeId === null означает «тип не проставлен».
 */
export type PeopleView = { kind: 'all' } | { kind: 'employment'; typeId: string | null };

export const DEFAULT_VIEW: PeopleView = { kind: 'all' };

/** Слаг представления для адреса. */
export function viewSlug(view: PeopleView): string {
  if (view.kind === 'all') {
    return VIEW_ALL;
  }
  return view.typeId === null ? VIEW_NO_TYPE : `${TYPE_PREFIX}${view.typeId}`;
}

/** Разбор слага из адреса. null — такого представления в разделе нет. */
export function parseView(raw: string | undefined): PeopleView | null {
  if (raw === undefined || raw === VIEW_ALL) {
    return DEFAULT_VIEW;
  }
  if (raw === VIEW_NO_TYPE) {
    return { kind: 'employment', typeId: null };
  }
  if (raw.startsWith(TYPE_PREFIX) && raw.length > TYPE_PREFIX.length) {
    return { kind: 'employment', typeId: raw.slice(TYPE_PREFIX.length) };
  }
  return null;
}

export function sameView(left: PeopleView, right: PeopleView): boolean {
  if (left.kind !== right.kind) {
    return false;
  }
  return left.kind === 'all' || right.kind === 'all' || left.typeId === right.typeId;
}

// ── Ключи отбора ──────────────────────────────────────────────────────────────────────────
// Голая строка в разметке разъезжается с разбором адреса, поэтому ключи здесь константами.

export const FILTER_STATUS = 'status';
export const FILTER_SITE = 'site';
export const FILTER_DEBT = 'debt';
export const FILTER_CATEGORY = 'category';
/** Регион объекта. Юрлица среди отборов нет намеренно: оно у заказчика одно. */
export const FILTER_REGION = 'region';

// ── Вкладка состояния ─────────────────────────────────────────────────────────────────────

/**
 * Значение отбора по состоянию, которое значит «любое»: `?f.status=all`.
 *
 * Явный маркер нужен потому, что чистый адрес теперь значит «На вахте», и «все» больше нечем
 * записать пустотой. Кода `all` среди состояний человека нет (`contracts/enums/statuses.yaml`),
 * поэтому спутать его с настоящим состоянием нельзя.
 */
export const STATUS_ALL = 'all';

/** С какого состояния открывается реестр, когда адрес о нём молчит и выборка ничем не сужена. */
export const DEFAULT_STATUS: EmployeeStatus = 'on_shift';

/** Что показано в реестре по состоянию: одно из состояний либо все сразу. */
export type RegistryTab = EmployeeStatus | typeof STATUS_ALL;

function isSet(value: string | undefined): value is string {
  return value !== undefined && value !== '';
}

/**
 * Сужена ли выборка отборами, кроме поиска и состояния: объект, регион, разряд, долг, срез по
 * типу оформления.
 */
export function hasFilterNarrowing(view: PeopleView, search: SearchState): boolean {
  return (
    isSet(search.filters[FILTER_SITE]) ||
    isSet(search.filters[FILTER_REGION]) ||
    isSet(search.filters[FILTER_CATEGORY]) ||
    isSet(search.filters[FILTER_DEBT]) ||
    view.kind === 'employment'
  );
}

/**
 * Сужена ли выборка чем-то, кроме состояния: поиск, объект, регион, разряд, долг, срез по типу
 * оформления.
 *
 * Состояние сюда НЕ входит намеренно: вкладка делит уже выбранных людей, а не выбирает их.
 */
export function hasNarrowing(view: PeopleView, search: SearchState): boolean {
  return isSet(search.q) || hasFilterNarrowing(view, search);
}

/**
 * Какая вкладка состояния открыта по адресу. Единственное место, где это решается.
 *
 * Зависит ТОЛЬКО от адреса, а не от загруженных данных: иначе вкладка мигала бы, пока едет
 * ответ сервера. Непонятное значение (`?f.status=мусор`) читается как «все»: пустой экран из-за
 * опечатки в ссылке хуже, чем полный список, в котором человека видно.
 */
export function registryTab(view: PeopleView, search: SearchState): RegistryTab {
  const named = search.filters[FILTER_STATUS];
  if (isSet(named)) {
    return isEmployeeStatus(named) ? named : STATUS_ALL;
  }
  return hasNarrowing(view, search) ? STATUS_ALL : DEFAULT_STATUS;
}

/**
 * Та же вкладка состояния, но по адресу страницы, а не по разобранному маршруту раздела.
 *
 * Нужна панелям: каркас отдаёт им только опознаватель и «закрыть» (`panel-registry`), а смена
 * состояния обязана знать, на какой вкладке человек нажал кнопку, чтобы подтверждение назвало,
 * куда ушла строка. Адрес чужого раздела или непонятный путь читаются как чистый реестр.
 */
export function registryTabAtLocation(pathname: string, search: string): RegistryTab {
  const path = parsePath(pathname);
  const view =
    (path !== null && path.section === SECTION ? parseView(path.view) : null) ?? DEFAULT_VIEW;
  return registryTab(view, parseSearch(search));
}

// ── Вкладки карточки ──────────────────────────────────────────────────────────────────────

export const CARD_TABS = [
  'overview',
  'documents',
  'assignments',
  'payouts',
  'debts',
  'payslip',
  'history',
  // Доступ стоит последним и открывается по праву на учётные записи. Восьмая вкладка — это
  // ровно потолок правила 12, и девятой в карточке не будет: следующее, что сюда попросятся
  // положить, придётся класть внутрь существующей.
  'access',
] as const;
export type CardTab = (typeof CARD_TABS)[number];

export const DEFAULT_TAB: CardTab = 'overview';

export function cardTab(value: string | undefined): CardTab {
  return value !== undefined && (CARD_TABS as readonly string[]).includes(value)
    ? (value as CardTab)
    : DEFAULT_TAB;
}

// ── Панели-мастера ────────────────────────────────────────────────────────────────────────
// Все они панели, а не модалки: заведение человека, документа и назначения обратимо
// (правило 4). Модалка остаётся за необратимым — за увольнением.

export const PANEL_NEW_EMPLOYEE = 'new-employee';
export const PANEL_EDIT_EMPLOYEE = 'emp-edit';
export const PANEL_STATUS = 'emp-status';
export const PANEL_DOCUMENT = 'emp-doc';
/** Новое заявление о реквизитах выплаты (сценарий 75). */
export const PANEL_PAYOUT = 'emp-payout';
export const PANEL_ASSIGN = 'emp-assign';
export const PANEL_TRANSFER = 'asgn-transfer';
export const PANEL_CLOSE = 'asgn-close';
/** Продление назначения: новый последний день либо «без даты окончания» (19.10.2026). */
export const PANEL_EXTEND = 'asgn-extend';
export const PANEL_RATE = 'asgn-rate';

/** Идентификатор панели «Новый сотрудник», когда контекста для неё нет. */
export const NEW_EMPLOYEE_BLANK = 'new';

/**
 * Мастер «Пригласить выбранных»: раздать ссылки для входа пачкой.
 *
 * Панель, а не полоса в подвале списка: результат — список «фамилия и ссылка» на два десятка
 * строк, и в узкой колонке реестра он не помещается. Тот же приём, что у мастера
 * укомплектования: выделение уезжает памятью, панель открывается адресом.
 */
export const PANEL_INVITE = 'emp-invite';
/** Опознаватель панели, когда люди пришли выделением из реестра, а не одной строкой. */
export const INVITE_FROM_REGISTRY = 'выбор';

export function openInviteHref(route: PeopleRoute): string {
  const next = pushPanel(route.search, { view: PANEL_INVITE, id: INVITE_FROM_REGISTRY });
  return peopleHref(route.view, next, route.rowId);
}

/**
 * Мастер укомплектования, открытый из реестра: объект в нём выбирается, а не задан.
 *
 * Имя панели общее с разделом «Объекты» (`site-staff`): мастер один и тот же, меняется только
 * то, откуда пришли. Второй мастер «то же самое, но из реестра» означал бы вторую форму
 * тарифа и второй список кандидатов.
 */
export const PANEL_STAFF_POST = 'site-staff';
export const STAFF_FROM_REGISTRY = 'выбор';

export function openStaffHref(route: PeopleRoute): string {
  const next = pushPanel(route.search, { view: PANEL_STAFF_POST, id: STAFF_FROM_REGISTRY });
  return peopleHref(route.view, next, route.rowId);
}

export function peopleHref(view: PeopleView, search: SearchState, rowId?: string): string {
  return `${buildPath({ section: SECTION, view: viewSlug(view), rowId })}${buildSearch(search)}`;
}

/** Разобранный адрес раздела. */
export interface PeopleRoute {
  view: PeopleView;
  rowId: string | undefined;
  search: SearchState;
}

/** Открыть панель поверх текущего экрана, ничего в нём не меняя. */
export function openPanelHref(route: PeopleRoute, panel: string, id: string): string {
  return peopleHref(route.view, pushPanel(route.search, { view: panel, id }), route.rowId);
}

/** Ставит или снимает один отбор, не трогая остальные. */
export function setFilter(
  search: SearchState,
  key: string,
  value: string | undefined,
): SearchState {
  const filters: Record<string, string> = { ...search.filters };
  if (value === undefined || value === '') {
    delete filters[key];
  } else {
    filters[key] = value;
  }
  return { ...search, filters };
}

/**
 * Что переносится при смене представления. Отбор и поиск осмысленны в любом представлении
 * реестра и переносятся целиком: человек сузил список до «с долгом» и переключает тип
 * оформления, чтобы сравнить срезы. Открытая карточка при этом уходит — она принадлежит
 * прежнему срезу, и в новом её может не быть.
 */
export function switchViewHref(to: PeopleView, search: SearchState): string {
  return peopleHref(to, { ...search, tab: undefined, panels: [] });
}
